import { useState, type ReactNode } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import { TaskSidebarPanel } from '../TaskSidebarPanel';
import { CountTabLabel } from '../CountTabLabel';
import { AddVirtualAppointmentButton } from '../../../virtual-appointments/AddVirtualAppointmentButton';
import { AddVirtualAppointmentModal } from '../../../virtual-appointments/AddVirtualAppointmentModal';
import { PastVirtualAppointmentList } from '../../../virtual-appointments/PastVirtualAppointmentList';
import { PlannedVirtualAppointmentList } from '../../../virtual-appointments/PlannedVirtualAppointmentList';
import { VirtualAppointmentWorkList } from '../../../virtual-appointments/VirtualAppointmentWorkList';
import {
    VirtualAppointmentLoadError,
    VirtualAppointmentStackSkeleton
} from '../../../virtual-appointments/VirtualAppointmentSplitStates';
import type { PatientOpenVirtualAppointments } from '../../../virtual-appointments/usePatientOpenVirtualAppointments';
import type { VirtualAppointmentHandoffDto } from '../../../../services/virtualAppointments';
import { VaHandoffThread } from './VaHandoffThread';

interface TabBodyProps {
    vas: PatientOpenVirtualAppointments;
    children: ReactNode;
}

// Each tab scrolls on its own; all wait on the same fetch. Rows run edge to edge, no inner cards.
function TabBody({ vas, children }: TabBodyProps) {
    return (
        <OverlayScrollArea className="flex-1 min-h-0" trackInsetPx={6}>
            {vas.status === 'loading' && (
                <div className="p-3">
                    <VirtualAppointmentStackSkeleton />
                </div>
            )}
            {vas.status === 'error' && (
                <VirtualAppointmentLoadError
                    message="Couldn't load this patient's VAs."
                    onRetry={vas.retry}
                />
            )}
            {vas.status === 'ready' && children}
        </OverlayScrollArea>
    );
}

interface VaWorkPanelProps {
    patientId: string;
    vas: PatientOpenVirtualAppointments;
    currentAligner: number | null;
    totalAligners: number | null;
    /** UTC day the Treatment phase began, which week triggers count from; null when unknown. */
    treatmentStartDate: string | null;
    /** Whether the patient is linked to Dental Monitoring, which no-go triggers count on. */
    dmLinked: boolean;
    /** This round's escalations and hand backs, oldest first, pinned above the open VAs. */
    handoffs: readonly VirtualAppointmentHandoffDto[];
    onRetryRound?: () => void;
}

// The VA card of the workspace: this round's VAs to work, what is planned next, and past ones.
export function VaWorkPanel({
    patientId,
    vas,
    currentAligner,
    totalAligners,
    treatmentStartDate,
    dmLinked,
    handoffs,
    onRetryRound
}: VaWorkPanelProps) {
    const [adding, setAdding] = useState(false);
    const ready = vas.status === 'ready';
    const tabs = [
        {
            key: 'open',
            label: <CountTabLabel label="Open VAs" count={ready ? vas.open.length : null} />,
            content: (
                <div className="flex-1 min-h-0 flex flex-col">
                    {handoffs.length > 0 && <VaHandoffThread handoffs={handoffs} />}
                    <TabBody vas={vas}>
                        <VirtualAppointmentWorkList
                            patientId={patientId}
                            round={vas.round}
                            currentAligner={currentAligner}
                            totalAligners={totalAligners}
                            treatmentStartDate={treatmentStartDate}
                            dmLinked={dmLinked}
                            onRetryRound={onRetryRound}
                        />
                    </TabBody>
                </div>
            )
        },
        {
            key: 'planned',
            label: <CountTabLabel label="Planned VAs" count={ready ? vas.planned.length : null} />,
            content: (
                <div className="flex-1 min-h-0 flex flex-col">
                    <TabBody vas={vas}>
                        <PlannedVirtualAppointmentList
                            planned={vas.planned}
                            treatmentStartDate={treatmentStartDate}
                        />
                    </TabBody>
                    {/* Pinned under the scrolling list, so it stays put however many are planned. */}
                    <div className="flex-shrink-0 flex justify-end border-t border-gray-100 px-4 py-2.5">
                        <AddVirtualAppointmentButton onAdd={() => setAdding(true)} />
                    </div>
                </div>
            )
        },
        {
            key: 'history',
            label: <CountTabLabel label="History" count={ready ? vas.history.length : null} />,
            content: (
                <TabBody vas={vas}>
                    <PastVirtualAppointmentList past={vas.history} />
                </TabBody>
            )
        }
    ];
    return (
        <>
            <TaskSidebarPanel tabs={tabs} keepMounted />
            {adding && (
                <AddVirtualAppointmentModal
                    patientId={patientId}
                    treatmentStartDate={treatmentStartDate}
                    dmLinked={dmLinked}
                    aligners={{ current: currentAligner, total: totalAligners }}
                    onClose={() => setAdding(false)}
                />
            )}
        </>
    );
}
